import '../styles/App.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import React, { useState, useLayoutEffect, useEffect , useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ScrollToTop from './ScrollToTop';
import { Outlet } from 'react-router-dom';
import { enhanceSmartImages } from '../Utils/SmartImageEnhancer';
import HomePage from './HomePage';
import NavBar from './NavBar';
import DropdownPortal from './DropdownPortal';
import Lenis from 'lenis'
import ChatButton    from './ChatButton';
import SellSurplus from './SellSurplus';
import SellTransformers from './SellTransformers';
import SellPVF from './SellPVF';
import SellElectrical from './SellElectrical';
import KvaCalculator from './KvaCalculator';
import FaultCurrentCalculator from './FaultCurrentCalculator';
import RoiCalculator from './RoiCalculator';
import TransformerGuides from './TransformerGuides';
import PvfGuides from './PvfGuides';
import PvfSizingCalculator from './PvfSizingCalculator';
import ElectricalGuides from './ElectricalGuides';
import UnderConstruction from './UnderConstruction';
import AboutUs from './AboutUs';
import PadmountArticle from './Articles/PadmountArticle';
import PolemountArticle from './Articles/PolemountArticle';
import SubstationArticle from './Articles/SubstationArticle';
import LowVoltageDryTypeArticle from './Articles/LowVoltageDryTypeArticle';
import MediumVoltageDryTypeArticle from './Articles/MediumVoltageDryTypeArticle';
import CastCoilArticle from './Articles/CastCoilArticle';
import PVFArticle from './Articles/PVFArticle';
import MetalEnclosedSwitchgearArticle from './Articles/MetalEnclosedSwitchgearArticle';
import MaximizeROIArticle from './Articles/MaximizeROIArticle';
import TransformerMaintenanceArticle from './Articles/TransformerMaintenanceArticle';
import UltimateGuidePVF from './Articles/UltimateGuidePVF';
import SafetyAndCompliance from './SafetyAndCompliance';
import History from './History';
import Contact from './Contact';
import FAQ from './FAQ';
import PaymentTerms from './PaymentTerms';
import ShippingTerms from './ShippingTerms';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';


gsap.registerPlugin(ScrollTrigger);

// Define the router with createBrowserRouter
const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "sell-surplus", element: <SellSurplus /> },
      { path: "sell-transformers", element: <SellTransformers /> },
      { path: "sell-pvf", element: <SellPVF /> },
      { path: "sell-electrical", element: <SellElectrical /> },
      { path: "kva-calculator", element: <KvaCalculator /> },
      { path: "fault-current-calculator", element: <FaultCurrentCalculator /> },
      { path: "roi-calculator", element: <RoiCalculator /> },
      { path: "transformer-guides", element: <TransformerGuides /> },
      { path: "pvf-guides", element: <PvfGuides /> },
      { path: "pvf-sizing", element: <PvfSizingCalculator /> },
      { path: "electrical-guides", element: <ElectricalGuides /> },
      { path: "under-construction", element: <UnderConstruction /> },
      { path: "about-us", element: <AboutUs /> },
      { path: "safety-and-compliance", element: <SafetyAndCompliance /> },
      { path: "articles/3-phase-padmount", element: <PadmountArticle /> },
      { path: "articles/3-phase-polemount", element: <PolemountArticle /> },
      { path: "articles/substation-transformer", element: <SubstationArticle /> },
      { path: "articles/low-voltage-transformer", element: <LowVoltageDryTypeArticle /> },
      { path: "articles/medium-voltage-transformer", element: <MediumVoltageDryTypeArticle /> },
      { path: "articles/cast-coil-transformer", element: <CastCoilArticle /> },
      { path: "articles/PVF", element: <PVFArticle /> },
      { path: "articles/metal-enclosed-switchgear", element: <MetalEnclosedSwitchgearArticle /> },
      { path: "blog/maximize-roi", element: <MaximizeROIArticle /> },
      { path: "blog/transformer-maintenance", element: <TransformerMaintenanceArticle /> },
      { path: "blog/ultimate-pvf-guide", element: <UltimateGuidePVF /> },
      { path: "company-history", element: <History /> },
      { path: "contact-us", element: <Contact /> },
      { path: "faq", element: <FAQ /> },
      { path: "payment-terms-and-conditions", element: <PaymentTerms /> },
      { path: "shipping-terms-and-conditions", element: <ShippingTerms /> },
    ]
  }
], {
  basename: "/"
});

// Layout component to wrap shared elements like NavBar, Dropdown, etc.
function AppLayout() {
  const [navHeight, setNavHeight] = useState(100);
  const [activeMenu, setActiveMenu] = useState(null);
  const [dropdownOffset, setDropdownOffset] = useState(null);
  const contentRef = useRef(null);
  const lenisRef = useRef(null); 
  const location = useLocation();

  useLayoutEffect(() => {
    enhanceSmartImages({ devLog: false });
  }, []);

  useEffect(() => {
    // 1) create a Lenis instance
  // Use native scroll on phones/tablets (coarse pointer)
   const isTouch = window.matchMedia('(pointer: coarse)').matches;
   if (isTouch) return; // do not init Lenis on mobile

   // Desktop/laptop: keep Lenis
   const lenis = new Lenis({
     duration: 0.8,
     easing: t => (--t)*t*t+1,
     orientation: 'vertical',
     direction:   'vertical',
     gestureOrientation: 'vertical',
     smoothWheel: true,
     smoothTouch: false,
     wheelMultiplier: .9,
     touchMultiplier: .9,
     infinite: false
   });

  // keep GSAP’s ScrollTrigger in sync with Lenis
  lenis.on('scroll', () => ScrollTrigger.update());

    // 2) create RAF loop
    function raf(time) {
      lenis.raf(time)
      requestAnimationFrame(raf)
    }
    requestAnimationFrame(raf)

    // optional cleanup
    return () => {
      lenis.destroy()
    }
  }, [])

  return (
    <>
      {/* 1) Fixed nav bar at the top */}
      <NavBar 
        activeMenu={activeMenu} 
        setActiveMenu={setActiveMenu}
        setDropdownOffset={setDropdownOffset}
        onHeightChange={setNavHeight}
        contentRef={contentRef}
        lenisRef={lenisRef}
      />

      {/* 2) Dropdown container (renders menus) */}
      <DropdownPortal 
        activeMenu={activeMenu} 
        setActiveMenu={setActiveMenu} 
        dropdownOffset={dropdownOffset}
        baseTop={navHeight}
      />

      <div ref={contentRef} id="main-content">
        <ScrollToTop />
        
        {/* Wrap Outlet with a keyed div for remounting on route change */}
        <div key={location.pathname} className="fade-in">
          <Outlet /> {/* This renders the child routes */}
        </div>
      </div>
      <ChatButton />
    </>
  );
}

function App() {
  return (
      <RouterProvider router={router} />
  );
}

export default App;